<script setup lang="ts">
const { t } = useI18n()
const display = useDisplay()
const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const systemStore = useSystemStore()

// 로그아웃 확인 모달 (DESIGN_SPEC.md 기준 — 배경 클릭/취소/확인 3가지 닫기 경로)
const logoutConfirmOpen = ref(false)
function confirmLogout() {
  logoutConfirmOpen.value = false
  userStore.logout()
}

const isTabletWidth = computed(() => {
  const width = display.width.value
  return width >= 800 && width <= 1100
})

// 사용자 타입에 따른 동적 버튼 텍스트
const campaignButtonText = computed(() => {
  if (!userStore.userStoreData)
    return ''

  const userType = userStore.userStoreData.userType || userStore.userStoreData.type

  if (userType === 'creator') {
    return '캠페인 보기'
  }
  else if (userType === 'advertiser') {
    return '크리에이터 보기'
  }
  return ''
})

// 캠페인/크리에이터 보기 버튼 클릭 핸들러
function handleCampaignButton() {
  if (!userStore.userStoreData)
    return

  const userType = userStore.userStoreData.userType || userStore.userStoreData.type

  if (userType === 'creator') {
    router.push('/creator/campaigns')
  }
  else if (userType === 'advertiser') {
    router.push('/advertiser/campaigns')
  }
}

// 컴포넌트 마운트 시 사용자 상태 확인
onMounted(async () => {
  try {
    await userStore.getCurrentUser()
  }
  catch (error) {
    console.warn('AppBar: 현재 사용자 확인 중 오류:', error)
  }
})

// 사용자 로그인 상태를 간단하고 명확하게 추적
const isLoggedIn = computed(() => {
  return !!userStore.userStoreData
})

function handleMenuClick(title: string) {
  switch (title) {
    case t('navigation.creator'):
      router.push('/creator')
      break
    case t('navigation.brand'):
      router.push('/advertiser')
      break
    case t('navigation.inquiry'):
      router.push('/inquiry')
      break
    case t('navigation.portfolio'):
      router.push('/portfolio')
      break
    case t('navigation.about'):
      router.push('/introduce')
      break
    case t('navigation.reviews'):
      router.push('/reviews')
      break
    case t('navigation.mypage'):
      if (isLoggedIn.value) {
        router.push('/myHome')
      }
      else {
        router.push('/user/login')
      }
      break
  }
}

// 메뉴 활성화 상태 확인 함수들
function isMenuActive(menuTitle: string): boolean {
  switch (menuTitle) {
    case t('navigation.creator'): return route.path.startsWith('/creator')
    case t('navigation.brand'): return route.path.startsWith('/advertiser')
    case t('navigation.inquiry'): return route.path === '/inquiry'
    case t('navigation.portfolio'): return route.path.startsWith('/portfolio')
    case t('navigation.about'): return route.path === '/introduce'
    case t('navigation.reviews'): return route.path === '/reviews'
    case t('navigation.mypage'): return route.path === '/myHome'
    default: return false
  }
}

// 모바일 "전체 메뉴" 시트 하단의 계정 메뉴. 기존 AppBar 데스크톱 우측 버튼들과 동일한 동작을 연결한다.
const accountItems = computed(() => {
  if (!isLoggedIn.value) {
    return [
      { key: 'action:login', label: t('navigation.login') },
      { key: 'action:register', label: t('navigation.register') },
      { key: 'action:contact', label: t('navigation.contact') },
    ]
  }
  const items = []
  if (campaignButtonText.value)
    items.push({ key: 'action:campaign', label: campaignButtonText.value })
  items.push(
    { key: 'action:contact', label: t('navigation.contact') },
    { key: 'action:logout', label: t('navigation.logout') },
  )
  return items
})

function handleGnbSelect(key: string) {
  switch (key) {
    case 'action:login':
      router.push('/user/login')
      break
    case 'action:register':
      router.push('/user/register')
      break
    case 'action:contact':
      router.push('/inquiry#inquiry-section')
      break
    case 'action:campaign':
      handleCampaignButton()
      break
    case 'action:logout':
      logoutConfirmOpen.value = true
      break
    default: handleMenuClick(key)
  }
}

// GNB Pill 메뉴 항목. 라우팅은 기존 handleMenuClick(title)이 그대로 처리한다.
const gnbItems = computed(() => systemStore.clientDrawerRightMenuList.map((menu: any) => ({
  key: menu.title,
  label: menu.title,
  active: isMenuActive(menu.title),
})))
</script>

<template>
  <v-app-bar
    id="appBar"
    class="app-bar-fixed"
    :height="display.width.value >= 800 ? 132 : 112"
    flat
    app
    fixed
    color="white"
  >
    <template #default>
      <v-container class="d-flex flex-column gnb-shell ma-0 pa-0" fluid>
        <!-- 데스크톱 레이아웃 -->
        <template v-if="display.width.value >= 800">
          <!-- 1행: 왼쪽 여백 / 로고(중앙) / 언어·로그인·문의하기(오른쪽) -->
          <v-container class="d-flex align-center gnb-identity-row px-6" fluid style="height: 76px;">
            <div class="gnb-logo-spacer" style="flex: 1 1 0%; min-width: 0;" />

            <!-- 로고 (중앙 정렬) -->
            <NuxtLink to="/" class="d-flex align-center gnb-brand" style="flex: none;">
              <v-img
                src="/logo_black.png"
                height="26"
                width="38"
                contain
                alt="잉 로고"
              />
            </NuxtLink>

            <div class="d-flex align-center gnb-account justify-end" style="flex: 1 1 0%; min-width: 0; gap: 12px;">
              <SiteTranslateWidget inline />
              <template v-if="!isLoggedIn">
                <span class="gnb-text-link" @click="router.push('/user/login')">{{ t('navigation.login') }}</span>
                <span class="gnb-text-link" @click="router.push('/user/register')">{{ t('navigation.register') }}</span>
              </template>
              <template v-else>
                <span
                  v-if="campaignButtonText && !isTabletWidth"
                  class="gnb-text-link"
                  @click="handleCampaignButton"
                >
                  {{ campaignButtonText }}
                </span>
                <span
                  class="gnb-text-link"
                  @click="logoutConfirmOpen = true"
                >
                  {{ t('navigation.logout') }}
                </span>
              </template>

              <v-btn
                color="#111"
                variant="flat"
                class="font-weight-bold"
                rounded="pill"
                slim
                @click="router.push('/inquiry#inquiry-section')"
              >
                <span class="text-white">{{ t('navigation.contact') }}</span>
              </v-btn>
            </div>
          </v-container>

          <!-- 2행: 카테고리 메뉴 (Pill GNB, 가운데 정렬. 1024 미만은 "전체 메뉴" 바텀시트) -->
          <v-container class="d-flex align-center gnb-menu-row px-6 justify-center" fluid style="height: 56px;">
            <AppGnb :items="gnbItems" @select="handleMenuClick" />
          </v-container>
        </template>

        <!-- 모바일 레이아웃 -->
        <template v-else>
          <!-- 1행: 로고(가운데) + 번역 위젯(우측), 2행: 메뉴 바("전체 메뉴" 바텀시트에 로그인·문의 등 계정 메뉴 포함) -->
          <v-container class="gnb-identity-row d-flex align-center px-4" fluid style="height: 60px;">
            <div class="gnb-logo-spacer" style="flex: 1 1 0%;" />
            <NuxtLink to="/" class="gnb-brand d-flex align-center" style="flex: none;">
              <v-img
                src="/logo_black.png"
                height="22"
                width="32"
                contain
                alt="잉 로고"
              />
            </NuxtLink>
            <div class="gnb-account d-flex align-center justify-end" style="flex: 1 1 0%;">
              <SiteTranslateWidget inline />
            </div>
          </v-container>
          <v-container class="d-flex align-center gnb-menu-row px-4 justify-center" fluid style="height: 52px;">
            <AppGnb :items="gnbItems" :extra-items="accountItems" @select="handleGnbSelect" />
          </v-container>
        </template>
      </v-container>
    </template>
  </v-app-bar>

  <!-- 로그아웃 확인 모달 -->
  <v-dialog v-model="logoutConfirmOpen" max-width="320">
    <v-card class="pa-6 text-center" style="border-radius: 12px;">
      <p class="font-weight-bold font-pretendard text-body-1 mb-6">
        로그아웃 하시겠어요?
      </p>
      <div class="d-flex ga-2">
        <v-btn variant="outlined" class="flex-1-1" style="border-color: #ddd; color: #333;" @click="logoutConfirmOpen = false">
          취소
        </v-btn>
        <v-btn color="#111" class="flex-1-1" @click="confirmLogout">
          <span class="text-white">로그아웃</span>
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* 모바일 햄버거 메뉴 아이콘 스타일 */
.mobile-menu-icon {
  z-index: 1001 !important;
  position: relative;
}

/* AppBar 고정 스타일 */
.app-bar-fixed {
  z-index: 1000 !important;
  border-bottom: 1px solid #eee;
}

.gnb-menu-row {
  border-top: 1px solid #f2f2f2;
}

.gnb-text-link {
  color: #555;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.gnb-text-link:hover {
  color: #111;
}
</style>
